import {
  ArrowRightStartOnRectangleIcon,
  BookOpenIcon,
  ChatBubbleLeftRightIcon,
  DocumentTextIcon,
  MapIcon,
  NewspaperIcon,
  UserGroupIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import React from 'react';
import { useLocation } from 'react-router';

import { AuthenticatedRoute } from '@/app/constant';
import { Dropdown } from '@/components/Dropdown';
import { Hexagon } from '@/components/Hexagon';
import { MenuProps } from '@/components/Menu';
import { useGetCollaboratorsListFromAccounts } from '@/hooks/useAccounts';
import { useFlag } from '@/hooks/useFlags';
import { useMy } from '@/hooks/useMy';
import Avatar from '@/sections/topNavBar/Avatar';
import { useAuth } from '@/state/auth';

export function AccountDropdown() {
  const location = useLocation();
  const {
    friend,
    stopImpersonation,
    me: primaryEmail,
    logout,
    isPraetorianUser,
    ssoDomain,
    userType,
  } = useAuth();

  const { enablePersonalTenancy } = useFlag({ doNotImpersonate: true });

  const { data: accounts = [] } = useMy(
    { resource: 'account' },
    { doNotImpersonate: true, enabled: !isPraetorianUser }
  );

  const { data: mySettings } = useMy(
    { resource: 'setting' },
    { doNotImpersonate: true }
  );

  const collaboratorAccounts = useGetCollaboratorsListFromAccounts(accounts);

  // OIDC/SAML SSO users sign in as the tenant itself, so their account list
  // never contains a record for their own tenant — a single linked tenant
  // (e.g. one subtenant) already means they can switch tenants. Password and
  // Google users have their own tenant in the list, hence the higher threshold.
  const otherTenantCount = collaboratorAccounts.filter(
    acc => acc.name !== primaryEmail
  ).length;
  const hasMultiTenantAccess =
    !isPraetorianUser &&
    otherTenantCount > (userType === 'sso-customer' ? 0 : 1);

  const showPersonalAccount =
    isPraetorianUser || enablePersonalTenancy || !!ssoDomain;

  const displayNameSetting = mySettings?.find(s => s.name === 'display-name');
  const personalAccountLabel =
    (typeof displayNameSetting?.value === 'string'
      ? displayNameSetting.value
      : '') || primaryEmail;

  return (
    <div data-testid="account-dropdown">
      <Dropdown
        className="rounded-[2px] p-0 text-base"
        styleType="none"
        menu={{
          className: 'w-64',
          items: [
            ...(!isPraetorianUser && primaryEmail !== 'admin@praetorian.com'
              ? [
                  {
                    label: 'Organization Settings',
                    icon: <UserIcon />,
                    to: {
                      pathname: AuthenticatedRoute.SETTINGS,
                      search: '?tab="orgSettings"',
                    },
                    relative: 'path' as const,
                  } as MenuProps['items'][number],
                ]
              : []),
            ...(showPersonalAccount
              ? [
                  {
                    label: personalAccountLabel,
                    icon: (
                      <Avatar
                        email={primaryEmail}
                        className="size-5 max-w-max scale-125 rounded-full"
                      />
                    ),
                    onClick: () => {
                      stopImpersonation(
                        AuthenticatedRoute.INSIGHTS,
                        false,
                        enablePersonalTenancy || !!ssoDomain
                      );
                    },
                  },
                ]
              : []),
            ...(isPraetorianUser || hasMultiTenantAccess
              ? [
                  {
                    label: isPraetorianUser
                      ? 'Customer Management'
                      : 'Tenant Management',
                    icon: isPraetorianUser ? (
                      <div className="flex size-5 max-w-max scale-125 items-center justify-center">
                        <img
                          src="/icons/praetorian-icon.svg"
                          alt="Praetorian"
                          className="h-full w-full object-contain"
                        />
                      </div>
                    ) : (
                      <UserGroupIcon />
                    ),
                    onClick: async () => {
                      stopImpersonation(
                        AuthenticatedRoute.CUSTOMER_MANAGEMENT,
                        true,
                        enablePersonalTenancy || hasMultiTenantAccess
                      );
                    },
                    relative: 'path' as const,
                  },
                  {
                    label: 'Divider',
                    type: 'divider' as const,
                  },
                ]
              : []),
            ...(!isPraetorianUser
              ? [
                  {
                    label: 'Shared Files',
                    icon: <DocumentTextIcon />,
                    to: AuthenticatedRoute.FILES,
                    relative: 'path' as const,
                  },
                ]
              : []),
            {
              label: 'Documentation',
              icon: <BookOpenIcon />,
              to: 'https://docs.praetorian.com',
            },
            {
              label: 'Feedback',
              icon: <ChatBubbleLeftRightIcon />,
              to: 'https://portal.praetorian.com',
            },
            {
              label: 'Roadmap',
              icon: <MapIcon />,
              to: 'https://portal.praetorian.com/roadmap',
            },
            {
              label: 'Changelog',
              icon: <NewspaperIcon />,
              to: 'https://portal.praetorian.com/changelog',
            },
            {
              label: 'Divider',
              type: 'divider' as const,
            },
            {
              label: 'User Profile',
              icon: (
                <Avatar
                  email={primaryEmail}
                  className="size-5 max-w-max scale-125 rounded-full"
                />
              ),
              ...(isPraetorianUser
                ? {
                    onClick: () => {
                      stopImpersonation(
                        AuthenticatedRoute.CUSTOMER_MANAGEMENT_USER_SETTINGS,
                        true
                      );
                    },
                  }
                : {
                    to: AuthenticatedRoute.PROFILE,
                    relative: 'path' as const,
                  }),
            },
            {
              label: 'Divider',
              type: 'divider' as const,
            },
            {
              label: 'Log Out',
              icon: <ArrowRightStartOnRectangleIcon />,
              onClick: logout,
            },
          ],
          value: primaryEmail,
        }}
      >
        <Hexagon>
          {location.pathname.includes(AuthenticatedRoute.CUSTOMER_MANAGEMENT) &&
          isPraetorianUser ? (
            <img
              src="/icons/logo.png"
              alt="Praetorian"
              className="h-full w-full scale-100 object-contain"
            />
          ) : (
            <Avatar
              className="scale-100"
              email={friend || primaryEmail}
              isOrganization={!!friend}
            />
          )}
        </Hexagon>
      </Dropdown>
    </div>
  );
}
